<template>
    <div class="content d-flex flex-column flex-column-fluid mb-10" id="kt_content">
      <div class="toolbar" id="kt_toolbar">
        <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
          <div data-kt-swapper="true" data-kt-swapper-mode="prepend"
               data-kt-swapper-parent="{default: '#kt_content_container', 'lg': '#kt_toolbar_container'}"
               class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
            <h1 class="d-flex text-dark fw-bolder fs-3 align-items-center my-1">{{ $t('navigation.all_deliveries') }}</h1>
            <span class="h-20px border-gray-300 border-start mx-4 d-none d-md-block"></span>
            <ul class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-1 d-none d-md-flex">
              <li class="breadcrumb-item text-muted">
                <router-link to="/admin/dashboard" class="text-muted text-hover-primary">{{ $t('breadcrumbs.store.dashboard') }}</router-link>
                <span class="bullet bg-gray-200 w-5px h-2px m-2"></span>
              </li>
              <li class="breadcrumb-item text-dark">{{ $t('navigation.all_deliveries') }}</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="post d-flex flex-column-fluid" id="kt_post">
        <div id="kt_content_container" class="container-xxl">
          <div class="card card-flush">

            <div class="card-header border-0 pt-6">
              <div class="card-title">
                <div class="d-flex align-items-center">
                  <div class="card-icon-delivery bg-light-success me-3">
                    <i class="bi bi-truck text-success fs-2"></i>
                  </div>
                  <div>
                    <h3 class="fw-bold mb-0 fs-4">{{ $t('navigation.all_deliveries') }}</h3>
                    <span class="text-muted fs-7">{{ $t('delivery.admin_subtitle') }}</span>
                  </div>
                </div>
              </div>
              <div class="card-toolbar">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <Select2
                    v-model="statusFilter"
                    :isRequired="false"
                    :multiple="false"
                    :show-search="false"
                    :placeholderText="$t('delivery.all')"
                    :selectClass="'form-select form-select-solid kt_select2 w-175px'"
                    containerClass="d-flex flex-column fv-row"
                    name="status_filter"
                  >
                    <option value="">{{ $t('delivery.all') }}</option>
                    <option value="PENDING">{{ $t('delivery.pending') }}</option>
                    <option value="DELIVERED">{{ $t('delivery.delivered') }}</option>
                  </Select2>

                  <Select2
                    v-model="userFilter"
                    :isRequired="false"
                    :multiple="false"
                    :show-search="false"
                    :placeholderText="$t('delivery.deliverer')"
                    :selectClass="'form-select form-select-solid kt_select2 w-200px'"
                    containerClass="d-flex flex-column fv-row"
                    name="user_filter"
                  >
                    <option value="all">{{ $t('delivery.all_deliverers') }}</option>
                    <option v-for="u in deliveryUsers" :key="u.id" :value="u.id">{{ u.fullname }}</option>
                  </Select2>
                </div>
              </div>
            </div>

            <div class="card-body pt-0">
              <div class="table-responsive">
                <table class="table align-middle table-row-bordered fs-6 gy-3" id="kt_datatable_all_deliveries">
                  <thead>
                    <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                      <th class="min-w-120px">{{ $t('delivery.order_number') }}</th>
                      <th class="min-w-130px">{{ $t('delivery.customer') }}</th>
                      <th class="min-w-150px">{{ $t('delivery.address') }}</th>
                      <th class="min-w-130px">{{ $t('delivery.deliverer') }}</th>
                      <th class="min-w-120px">{{ $t('delivery.shipping_date') }}</th>
                      <th class="min-w-100px text-center">{{ $t('delivery.status') }}</th>
                      <th class="min-w-80px text-end">{{ $t('delivery.actions') }}</th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { BASE_URL } from '@/services/api'
import Ls from '@/services/ls'
import i18n from '@/plugins/i18n'
import Select2 from '@/components/Select2.vue'

const store  = useStore()
const table  = ref(null)

const statusFilter  = ref('')
const userFilter    = ref('all')
const deliveryUsers = ref([])

const locale = computed(() => i18n.global.locale)
const datatableLocaleUrl = computed(() =>
  locale.value === 'fr'
    ? '//cdn.datatables.net/plug-ins/1.10.20/i18n/French.json'
    : '//cdn.datatables.net/plug-ins/1.10.20/i18n/English.json'
)

function formatDate(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function reloadTable() {
  if (!table.value) return
  table.value.ajax.url(buildUrl()).load()
}

function buildUrl() {
  let url = BASE_URL + '/get/all/deliveries/datatable?1=1'
  if (statusFilter.value) url += `&status=${statusFilter.value}`
  if (userFilter.value && userFilter.value !== 'all') url += `&delivery_user_id=${userFilter.value}`
  return url
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function renderAddress(addr) {
  if (!addr) return '<span class="text-muted">—</span>'
  const parts = [addr.quartier, addr.city].filter(Boolean)
  const location = parts.join(', ') || addr.location_label || '—'
  const street = addr.address && !UUID_RE.test(addr.address.trim())
    ? `<br><small class="text-muted"><i class="bi bi-house me-1"></i>${addr.address}</small>`
    : ''
  const landmark = addr.point_de_repere
    ? `<br><small class="text-muted fst-italic"><i class="bi bi-signpost-2 me-1"></i>${addr.point_de_repere}</small>`
    : ''
  const mapsBtn = (addr.latitude && addr.longitude)
    ? `<br><a href="https://www.google.com/maps?q=${addr.latitude},${addr.longitude}" target="_blank" class="btn btn-xs btn-light-primary mt-1 px-2 py-1 fs-8"><i class="bi bi-map me-1"></i>${i18n.global.t('destockage.view_on_map')}</a>`
    : ''
  return `<span class="text-gray-800">${location}</span>${street}${landmark}${mapsBtn}`
}

function initTable() {
  const AUTH_TOKEN = Ls.get('auth.token')

  table.value = $('#kt_datatable_all_deliveries').DataTable({
    destroy: true,
    responsive: true,
    searchDelay: 500,
    processing: true,
    serverSide: true,
    pageLength: Ls.get('pageLength') ?? 10,
    lengthMenu: [5, 10, 25, 50, 100],
    order: [[4, 'desc']],
    ajax: {
      url: buildUrl(),
      type: 'GET',
      beforeSend: (req) => req.setRequestHeader('Authorization', 'Bearer ' + AUTH_TOKEN),
    },
    columns: [
      { data: 'checkout_number' },
      { data: 'customer_name' },
      { data: 'shipping_address', orderable: false },
      { data: 'delivery_person' },
      { data: 'delivery_date' },
      { data: 'status_label', orderable: false },
      { data: 'checkout_id',  orderable: false },
    ],
    columnDefs: [
      {
        targets: 0,
        render: (val) => val ? `<span class="fw-bold text-gray-800">#${val}</span>` : '-',
      },
      {
        targets: 1,
        render: (val) => val ? `<span class="text-gray-800">${val}</span>` : '-',
      },
      {
        targets: 2,
        orderable: false,
        render: (val) => renderAddress(val),
      },
      {
        targets: 3,
        render: (val) => {
          if (!val) return `<span class="text-muted">—</span>`
          const badge = val.type === 'EXTERNAL' ? ` <span class="badge badge-light-warning fs-9">${i18n.global.t('destockage.external')}</span>` : ''
          return `<span class="text-gray-600">${val.name}</span>${badge}`
        },
      },
      {
        targets: 4,
        render: (val) => `<span class="text-gray-600">${formatDate(val)}</span>`,
      },
      {
        targets: 5,
        className: 'dt-center text-center',
        orderable: false,
        render: (t, a, row) => {
          const color = row.status_badge || 'secondary'
          const label = row.status_label || row.status || '-'
          return `<span class="btn btn-sm btn-light-${color}" style="font-size:12px;">${label}</span>`
        }
      },
      {
        targets: 6,
        className: 'text-end',
        render: (checkoutId) =>
          `<a href="/admin/ecommerce/orders/${checkoutId}/view/destockage"
              class="btn btn-icon btn-light-primary btn-sm"
              title="${i18n.global.t('delivery.view_details')}">
             <i class="bi bi-eye"></i>
           </a>`,
      },
    ],
    language: { url: datatableLocaleUrl.value },
  })
}

async function loadDeliveryUsers() {
  try {
    const res = await store.dispatch('ecommerce/getUsersForDelivery')
    deliveryUsers.value = res?.data || []
  } catch {
    // silence
  }
}

function initSelect2() {
  KTApp.initSelect2()
  $('.kt_select2').select2()
  $('.kt_select2').off('change.delivery').on('change.delivery', (e) => {
    switch (e.target.name) {
      case 'status_filter': statusFilter.value = e.target.value; break
      case 'user_filter':   userFilter.value   = e.target.value; break
    }
  })
}

watch(statusFilter, reloadTable)
watch(userFilter,   reloadTable)

onMounted(async () => {
  await loadDeliveryUsers()
  initTable()
  initSelect2()
})
</script>

<style scoped>
.badge-light-success   { background-color: rgba(76, 175, 80, 0.1);  color: #4CAF50; }
.badge-light-warning   { background-color: rgba(255, 152, 0, 0.1);  color: #FF9800; }
.badge-light-secondary { background-color: rgba(180, 180, 180, 0.15); color: #7e8299; }

.card-icon-delivery {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
